import { tsoPqTheme } from '@assets/style/customTsoPqtheme';
import { t } from 'i18next';

export type ReasonProps =
  | 'noError'
  | 'invalidMeasurementDevice'
  | 'invalidAggregationInterval'
  | 'cannotParse'
  | 'invalidColumnName'
  | 'invalidTimestamp'
  | 'conflictingRows'
  | 'alreadyImported'
  | 'applicationError';

export type StatusProps = 'error' | 'warning' | 'success' | 'inProgress' | 'onHold' | undefined;

export const statusChipOptions = {
  warning: {
    style: {
      background: tsoPqTheme.palette.customInformation.color_signal_warning_warning_bg_accent,
      color: tsoPqTheme.palette.customInformation.color_signal_warning_warning,
    },
    label: t('upload.importStatus.commonStatus.warning'),
  },
  inProgress: {
    style: {
      background: tsoPqTheme.palette.customInformation.color_signal_information_information_bg_accent,
      color: tsoPqTheme.palette.customInformation.color_signal_information_information,
    },
    label: t('upload.importStatus.commonStatus.inProgress'),
  },
  error: {
    style: {
      background: tsoPqTheme.palette.customError.color_signal_error_error_bg_light,
      color: tsoPqTheme.palette.customError.color_signal_error_error,
    },
    label: t('upload.importStatus.commonStatus.error'),
  },
  success: {
    style: {
      background: tsoPqTheme.palette.customInformation.color_signal_success_success_bg_accent,
      color: tsoPqTheme.palette.customInformation.color_signal_success_success,
    },
    label: t('upload.importStatus.commonStatus.success'),
  },
  onHold: {
    style: {
      background: tsoPqTheme.palette.background.neutral.color_neutral_background_bg_ntrl,
      color: tsoPqTheme.palette.customTextColor.color_neutral_font_ft_nrtrl_enabled,
    },
    label: t('upload.importStatus.commonStatus.onHold'),
  },
  default: {
    style: {
      background: tsoPqTheme.palette.background.neutral.color_neutral_background_bg_ntrl,
      color: tsoPqTheme.palette.customTextColor.color_neutral_font_ft_nrtrl_enabled,
    },
    label: '',
  },
};

export const statusMap = {
  validation: {
    0: 'inProgress',
    1: 'inProgress',
    2: 'success',
    3: 'error',
    4: 'warning',
  },
  import: {
    0: 'onHold',
    1: 'inProgress',
    2: 'error',
    3: 'success',
    4: 'warning',
    5: 'inProgress',
    6: 'inProgress',
  },
  plausibility: {
    0: 'onHold',
    1: 'inProgress',
    2: 'error',
    3: 'success',
    4: 'warning', //Show plausibility results
    5: '',
    6: 'error', //Show plausibility results
  },
  quantileCalculation: {
    0: 'onHold',
    1: 'inProgress',
    2: 'success',
    3: 'error',
  },
};

export const validationErrorMap = {
  0: 'noError',
  1: 'invalidMeasurementDevice',
  2: 'invalidAggregationInterval',
  3: 'cannotParse',
  4: 'invalidColumnName',
  5: 'invalidTimestamp',
  6: 'conflictingRows',
  7: 'alreadyImported',
  8: 'applicationError',
};

export const infoContentMaps = {
  validation: {
    inProgress: t('upload.detailedView.accordion.infoContent.validation.inProgress'),
    success: t('upload.detailedView.accordion.infoContent.validation.success'),
    warning: t('upload.detailedView.accordion.infoContent.validation.warning'),
    error: t('upload.detailedView.accordion.infoContent.validation.error'),
  },
  import: {
    onHold: t('upload.detailedView.accordion.infoContent.import.onHold'),
    inProgress: t('upload.detailedView.accordion.infoContent.import.inProgress'),
    error: t('upload.detailedView.accordion.infoContent.import.error'),
    success: t('upload.detailedView.accordion.infoContent.import.success'),
    warning: t('upload.detailedView.accordion.infoContent.import.warning'),
  },
  plausibility: {
    onHold: t('upload.detailedView.accordion.infoContent.plausibility.onHold'),
    inProgress: t('upload.detailedView.accordion.infoContent.plausibility.inProgress'),
    error: t('upload.detailedView.accordion.infoContent.plausibility.error'),
    success: t('upload.detailedView.accordion.infoContent.plausibility.success'),
    warning: t('upload.detailedView.accordion.infoContent.plausibility.warning'),
  },
  quantileCalculation: {
    onHold: t('upload.detailedView.accordion.infoContent.quantileCalculation.onHold'),
    inProgress: t('upload.detailedView.accordion.infoContent.quantileCalculation.inProgress'),
    success: t('upload.detailedView.accordion.infoContent.quantileCalculation.success'),
    error: t('upload.detailedView.accordion.infoContent.quantileCalculation.error'),
  },
};

export const quantileCalculationContent = {
  success: {
    title: t('upload.detailedView.accordion.infoContent.quantileCalculationContent.success.title'),
    content: t('upload.detailedView.accordion.infoContent.quantileCalculationContent.success.content'),
    label: t('upload.detailedView.accordion.infoContent.quantileCalculationContent.success.buttonLabel'),
  },
  error: {
    title: t('upload.detailedView.accordion.infoContent.quantileCalculationContent.error.title'),
    content: t('upload.detailedView.accordion.infoContent.quantileCalculationContent.error.content'),
    label: t('upload.detailedView.accordion.infoContent.quantileCalculationContent.error.buttonLabel'),
  },
};

export const validationCases = {
  warning: {
    invalidColumnName: {
      title: t('upload.detailedView.validityCheckDetails.unknownColumns'),
      content: t('upload.detailedView.validityCheckDetails.columnsDetails'),
    },
    invalidTimestamp: {
      title: t('upload.detailedView.validityCheckDetails.invalidTimesStamps'),
      content: t('upload.detailedView.validityCheckDetails.invalidTimestampDetails'),
    },
    conflictingRows: {
      title: t('upload.detailedView.validityCheckDetails.duplicateRows'),
      contetn: t('upload.detailedView.validityCheckDetails.duplicateRowDetails'),
    },
    alreadyImported: {
      title: t('upload.detailedView.validityCheckDetails.dataAlreadyUploaded'),
      content: t('upload.detailedView.validityCheckDetails.dataAlreadyUploadedDetails'),
    },
  },
  error: {
    invalidMeasurementDevice: {
      title: t('upload.detailedView.validityCheckDetails.deviceAndAggregation.missing'),
      content: t('upload.detailedView.validityCheckDetails.deviceAndAggregation.additional'),
      id: 1,
    },
    invalidAggregationInterval: {
      title: t('upload.detailedView.validityCheckDetails.deviceAndAggregation.missing'),
      content: t('upload.detailedView.validityCheckDetails.deviceAndAggregation.additional'),
      id: 2,
    },
    cannotParse: {
      title: t('upload.detailedView.validityCheckDetails.cannotParse'),
      content: t('upload.detailedView.validityCheckDetails.cannotParseDetails'),
      id: 3,
    },
    invalidColumnName: {
      title: t('upload.detailedView.validityCheckDetails.unknownColumns'),
      content: t('upload.detailedView.validityCheckDetails.columnsDetails'),
      id: 4,
    },
    invalidTimestamp: {
      title: t('upload.detailedView.validityCheckDetails.invalidTimesStamps'),
      content: t('upload.detailedView.validityCheckDetails.invalidTimestampDetails'),
      id: 5,
    },
    conflictingRows: {
      title: t('upload.detailedView.validityCheckDetails.duplicateRows'),
      contetn: t('upload.detailedView.validityCheckDetails.duplicateRowDetails'),
      id: 6,
    },
    alreadyImported: {
      title: t('upload.detailedView.validityCheckDetails.dataAlreadyUploaded'),
      content: t('upload.detailedView.validityCheckDetails.dataAlreadyUploadedDetails'),
      id: 7,
    },
    applicationError: {
      title: t('upload.detailedView.validityCheckDetails.applicationError'),
      content: t('upload.detailedView.validityCheckDetails.applicationErrorDetails'),
      id: 8,
    },
  },
};
